/**
 * Settings modal submit handler logic
 * Handles form validation, saving changes, and updating UI
 */

import { HangoutState } from '../../state/HangoutState.js';
import { fetchCharacterSprite } from '../character-manager.js';
import { storage } from '../../../utils/storage.js';
import { closeModal, openModal } from './modal-utils.js';
import { mapValueToTimeOfDay } from './settings-state.js';
import { resolveLatestCharacterSpriteData } from '../../shared/sprite-state.js';
import { persistCharacterSpriteMetaInConfig } from '../../shared/hangout-config-utils.js';
import { getTranslation } from './settings-dom-utils.js';
import { getCurrentLayoutMode, LAYOUT_MODES } from '../layout-mode.js';
import { getTranscriptForLayout } from '../../shared/history-resolution.js';

const JOIN_MESSAGE_STANDING_ACTION_ID = 2016;

const toCharacterId = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
};

const collectIdsFromCharacters = (characters = []) => {
    const ids = new Set();
    for (const character of characters) {
        const id = toCharacterId(character?.id ?? character?.characterId);
        if (id) ids.add(id);
    }
    return ids;
};

const collectIdsFromStage = () => {
    const ids = new Set();
    document.querySelectorAll('.character-image[data-character-id], [class*="character-image-"][data-character-id]').forEach((element) => {
        const id = toCharacterId(element.getAttribute('data-character-id'));
        if (id) ids.add(id);
    });
    return ids;
};

const collectIdsFromDialogue = (messages = []) => {
    const ids = new Set();
    for (const message of messages) {
        if (message?.role !== 'assistant') continue;
        if (message.isJoinMessage === true || message.isLeaveMessage === true || message.isCharacterChange === true) continue;
        const id = toCharacterId(message.characterId ?? message.character_id);
        if (id) ids.add(id);
    }
    return ids;
};

const resolvePreviousCrewCharacters = (hangoutConfig, hangoutState) => {
    const fromConfig = Array.isArray(hangoutConfig?.allCharacters) ? hangoutConfig.allCharacters : [];
    const fromState = Array.isArray(hangoutState._getConfig?.()?.allCharacters)
        ? hangoutState._getConfig().allCharacters
        : [];
    const fromSession = Array.isArray(hangoutState.getCurrentSession?.()?.allCharacters)
        ? hangoutState.getCurrentSession().allCharacters
        : [];
    const byId = new Map();
    for (const character of [...fromConfig, ...fromState, ...fromSession]) {
        const id = toCharacterId(character?.id);
        if (!id || byId.has(id)) continue;
        byId.set(id, character);
    }
    return [...byId.values()];
};

const resolvePresentCharacterIds = (previousCrew, hangoutState) => {
    const present = collectIdsFromCharacters(previousCrew);
    collectIdsFromStage().forEach((id) => present.add(id));
    collectIdsFromDialogue(hangoutState.getMessages?.() || []).forEach((id) => present.add(id));
    return present;
};

const isRosterReplayMessage = (message) =>
    message?.isJoinMessage === true
    || message?.isLeaveMessage === true
    || message?.isCharacterChange === true;

const isRealDialogueTurn = (message) => {
    if (!message) return false;
    if (message.role === 'user') return true;
    if (message.role !== 'assistant') return false;
    return !isRosterReplayMessage(message);
};

const shouldCancelRosterPair = (messages, characterId) => {
    const list = Array.isArray(messages) ? messages : [];
    const id = toCharacterId(characterId);
    if (!id) return false;
    let lastJoinIndex = -1;
    for (let index = list.length - 1; index >= 0; index -= 1) {
        const message = list[index];
        if (message?.isJoinMessage !== true) continue;
        if (toCharacterId(message.characterId ?? message.character_id) !== id) continue;
        lastJoinIndex = index;
        break;
    }
    if (lastJoinIndex < 0) return false;
    for (let index = lastJoinIndex + 1; index < list.length; index += 1) {
        if (isRealDialogueTurn(list[index])) return false;
    }
    return true;
};

const appendRosterNotice = async (message) => {
    if (getCurrentLayoutMode() !== LAYOUT_MODES.CHATBOT) return;
    try {
        const { getDisplayManager } = await import('../message-utils.js');
        const displayManager = await getDisplayManager();
        if (displayManager?.isAvailable?.() && typeof displayManager.createRosterNotice === 'function') {
            await displayManager.createRosterNotice(message);
        }
    } catch (error) {
        console.warn('Failed to render roster notice:', error);
    }
};

const getCachedLocationName = (locationId, fallback) => {
    const cachedData = storage.get('background_loc_json');
    if (cachedData && typeof cachedData === 'object' && cachedData[String(locationId)]) {
        return cachedData[String(locationId)];
    }
    return fallback;
};

/**
 * Validate if any selected outfits are unowned (require premium)
 * @param {Array<{charSelect: string, roleSelect: string}>} crewMap
 * @returns {Promise<Array<{characterName: string, outfitName: string}>>}
 */
export async function validateOutfitOwnership(crewMap) {
    const unownedOutfits = [];

    for (const { charSelect, roleSelect } of crewMap) {
        const charSelectEl = document.getElementById(charSelect);
        const roleSelectEl = document.getElementById(roleSelect);

        if (charSelectEl?.value && roleSelectEl?.value) {
            const selectedOption = roleSelectEl.querySelector(`option[value="${roleSelectEl.value}"]`);
            if (selectedOption?.getAttribute('data-unowned') === 'true') {
                const characterName = charSelectEl.selectedOptions[0]?.textContent || 'Character';
                const outfitName = selectedOption.textContent;
                unownedOutfits.push({ characterName, outfitName });
            }
        }
    }

    return unownedOutfits;
}

/**
 * Update validation message in real-time
 * @param {Array<{charSelect: string, roleSelect: string}>} crewMap
 */
export async function updateValidationMessage(crewMap) {
    const validationMessage = document.getElementById('outfit-validation-message');
    if (!validationMessage) return;

    const unownedOutfits = await validateOutfitOwnership(crewMap);

    if (unownedOutfits.length > 0) {
        const messageText = unownedOutfits.length === 1
            ? `The outfit "${unownedOutfits[0].outfitName}" requires a premium membership. Please select a different outfit or upgrade your membership.`
            : `The following outfits require a premium membership:\n${unownedOutfits.map(item => `• "${item.outfitName}"`).join('\n')}\n\nPlease select different outfits or upgrade your membership.`;

        validationMessage.innerHTML = messageText.replace(/\n/g, '<br>');
        validationMessage.classList.remove('is-hidden');
    } else {
        validationMessage.classList.add('is-hidden');
    }
}

const getScenarioGoalForSlot = (scenario, slotIndex, characterId = null) => {
    if (!scenario || typeof slotIndex !== 'number' || slotIndex < 0) return '';

    const goalBySlotKey = scenario[`goal_${slotIndex + 1}`];
    if (typeof goalBySlotKey === 'string' && goalBySlotKey.trim()) {
        return goalBySlotKey.trim();
    }

    const scenarioCharacters = Array.isArray(scenario.chars)
        ? scenario.chars
        : (Array.isArray(scenario.allCharacters) ? scenario.allCharacters : []);

    if (scenarioCharacters.length > 0) {
        if (characterId !== null && characterId !== undefined) {
            const normalizedId = String(characterId);
            const matchedCharacter = scenarioCharacters.find(char => String(char?.id) === normalizedId);
            if (typeof matchedCharacter?.goal === 'string' && matchedCharacter.goal.trim()) {
                return matchedCharacter.goal.trim();
            }
        }

        const slotGoal = scenarioCharacters[slotIndex]?.goal;
        if (typeof slotGoal === 'string' && slotGoal.trim()) {
            return slotGoal.trim();
        }
    }

    if (slotIndex === 0 && typeof scenario?.goal === 'string' && scenario.goal.trim()) {
        return scenario.goal.trim();
    }

    return '';
};

/**
 * Save character selections to storage and state
 * @param {Array} selections - Character selections
 * @param {string} sessionId - Current session ID
 * @param {string} scenarioId - Current scenario ID
 * @returns {Promise<string|null>} - Conversation ID or null on error
 */
export async function saveCharacterSelections(selections, sessionId, scenarioId = '1') {
    try {
        const { storageManager } = await import('../../../../core/storage/index.js');
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};

        const conversationId = sessionId || hangoutConfig.conversationId || null;

        const allCharacters = selections.map(selection => ({
            id: selection.id,
            name: selection.name,
            outfitId: selection.outfitId || 0,
            gender: selection.gender,
            isActive: true,
            ...(selection.actionId != null ? { actionId: selection.actionId } : {}),
            ...(selection.spriteState ? { spriteState: selection.spriteState } : {})
        }));

        // Fetch and store voice_id for each character
        try {
            const { HangoutService } = await import('../../services/HangoutService.js');
            const { getAllVoices, pickDefaultElevenLabsVoice } = await import('../../../services/voiceService.js');
            const { normalizeGender } = await import('../../../utils/genderUtils.js');
            const hangoutService = HangoutService.getInstance();
            const voiceIdPreferences = storage.get('characterVoiceIds') || {};
            
            for (const char of allCharacters) {
                if (!voiceIdPreferences[char.id]) {
                    try {
                        const characterDetails = await hangoutService.getCharacterDetails(char.id);
                        const genderSource = characterDetails || char;
                        const normalizedGender = normalizeGender(genderSource);
                        const voiceSubDir = normalizedGender === 'male' ? 'male_voices' : 'voices';
                        const availableVoices = await getAllVoices(voiceSubDir);

                        if (!availableVoices || availableVoices.length === 0) continue;

                        let voiceToStore = null;

                        if (characterDetails?.voice_id) {
                            const jsonVid = characterDetails.voice_id;
                            const jsonVidStr = String(jsonVid);
                            voiceToStore = availableVoices.find(v =>
                                v.provider_voice_id === jsonVid ||
                                String(v.provider_voice_id) === jsonVidStr ||
                                v.id === jsonVid ||
                                String(v.id) === jsonVidStr
                            );

                            if (!voiceToStore) {
                                console.warn(`[SettingsModal] Could not find voice mapping for provider_voice_id ${characterDetails.voice_id}`);
                            }
                        }

                        if (!voiceToStore) {
                            voiceToStore = pickDefaultElevenLabsVoice(availableVoices);
                        }

                        if (voiceToStore) {
                            voiceIdPreferences[char.id] = voiceToStore.id;
                            if (voiceToStore.provider) {
                                const providerPrefs = storage.get('characterVoiceProviders') || {};
                                providerPrefs[char.id] = voiceToStore.provider;
                                storage.set('characterVoiceProviders', providerPrefs);
                            }
                        }
                    } catch (error) {
                        console.warn(`[SettingsModal] Failed to fetch voice_id for character ${char.id}:`, error);
                    }
                }
            }
            
            storage.set('characterVoiceIds', voiceIdPreferences);
        } catch (error) {
            console.warn('[SettingsModal] Failed to fetch voice_ids:', error);
        }

        const updatedConfig = {
            ...hangoutConfig,
            conversationId,
            allCharacters,
            scenarioId: scenarioId || hangoutConfig.scenarioId
        };

        const existingConversationId = typeof hangoutConfig?.conversationId === 'string'
            ? hangoutConfig.conversationId.trim()
            : '';
        const incomingConversationId = typeof updatedConfig?.conversationId === 'string'
            ? updatedConfig.conversationId.trim()
            : '';
        if (!incomingConversationId && existingConversationId) {
            updatedConfig.conversationId = existingConversationId;
        }

        delete updatedConfig.characterId;
        delete updatedConfig.outfitId;

        await storageManager.setItem('currentHangoutConfig', updatedConfig);

        const hangoutState = HangoutState.getInstance();
        await hangoutState.setActiveCharacters(allCharacters);
        await hangoutState.setCharacters(allCharacters);

        return conversationId;
    } catch (error) {
        console.error('[SettingsModal] Error saving character selections:', error);
        return null;
    }
}

/**
 * Main submit handler for settings modal
 * @param {Object} params - Handler parameters
 * @param {Array} params.crewMap - Crew slot configuration
 * @param {Array} params.allCharacters - All available characters
 * @param {Object} params.scenario - Current scenario
 * @param {Object} params.storageManager - Storage manager instance
 * @param {HTMLElement} params.validationMessage - Validation message element
 * @param {function|null} params.handleEscape - Escape key handler to clean up
 * @param {function} params.setHandleEscape - Function to update handleEscape
 */
export async function handleSettingsSubmit({
    crewMap,
    allCharacters,
    scenario,
    storageManager,
    validationMessage,
    handleEscape,
    setHandleEscape
}) {
    const hangoutState = HangoutState.getInstance();

    try {
        // Validate outfit ownership first
        const unownedOutfits = await validateOutfitOwnership(crewMap);

        if (unownedOutfits.length > 0) {
            const messageText = unownedOutfits.length === 1
                ? `The outfit "${unownedOutfits[0].outfitName}" requires a premium membership. Please select a different outfit or upgrade your membership.`
                : `The following outfits require a premium membership:\n${unownedOutfits.map(item => `• "${item.outfitName}"`).join('\n')}\n\nPlease select different outfits or upgrade your membership.`;

            validationMessage.innerHTML = messageText.replace(/\n/g, '<br>');
            validationMessage.classList.remove('is-hidden');
            validationMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });

            try {
                const { showQuotaExhaustedModal } = await import('../../../utils/quotaErrorHandler.js');
                await showQuotaExhaustedModal();
            } catch (modalError) {
                console.warn('Could not show upgrade modal:', modalError);
            }

            return false; // Block save
        }

        validationMessage.classList.add('is-hidden');

        // Collect form data
        const scenarioDescription = document.getElementById('scenario-description-settings')?.value || '';
        const scenarioDescriptionTrimmed = scenarioDescription.trim();
        const scenarioDefaultDescription = (scenario?.description || scenario?.descr || '').trim();
        const isScenarioDescriptionCustom = Boolean(
            scenarioDescriptionTrimmed &&
            scenarioDescriptionTrimmed !== scenarioDefaultDescription
        );
        const scenarioDescriptionMetadataValue = isScenarioDescriptionCustom ? scenarioDescriptionTrimmed : null;
        const backgroundId = document.getElementById('generate-bg-settings')?.value || '';
        const timeOfDayValue = document.getElementById('time-of-day-settings')?.value || '0.1';
        const timeOfDayString = mapValueToTimeOfDay(timeOfDayValue);

        // Collect character data from visible slots only
        const convCharacters = [];
        const characterGoals = {};
        const customGoals = {};
        const parsedVisibleCount = Number.parseInt(document.getElementById('num-allCharacters-settings')?.value || '', 10);
        const visibleSlotCount = Number.isFinite(parsedVisibleCount)
            ? Math.min(Math.max(parsedVisibleCount, 1), crewMap.length)
            : crewMap.length;

        crewMap.forEach(({ charSelect, roleSelect }, index) => {
            if (index >= visibleSlotCount) return;
            const charId = document.getElementById(charSelect)?.value;
            const outfitId = document.getElementById(roleSelect)?.value;
            const goalValue = document.getElementById(`char-${index + 1}-goal-settings`)?.value || '';

            if (charId) {
                const character = allCharacters.find(c => c.id.toString() === charId) || {};
                const characterData = {
                    id: charId,
                    name: character.name || `Character ${index + 1}`,
                    outfitId: outfitId || 0,
                    gender: character.gender
                };

                if (goalValue.trim()) {
                    const normalizedGoal = goalValue.trim();
                    const scenarioGoal = getScenarioGoalForSlot(scenario, index, charId);
                    const isGoalCustom = normalizedGoal !== scenarioGoal;
                    characterData.goal = normalizedGoal;
                    customGoals[String(charId)] = isGoalCustom;
                    if (isGoalCustom) {
                        characterGoals[charId] = normalizedGoal;
                    }
                } else {
                    customGoals[String(charId)] = false;
                }

                convCharacters.push(characterData);
            }
        });

        // Close modal
        if (typeof handleEscape === 'function') {
            document.removeEventListener('keydown', /** @type {EventListener} */ (handleEscape));
            setHandleEscape(null);
        }
        closeModal('settings-modal');

        // 1. Save scenario description
        if (isScenarioDescriptionCustom) {
            await hangoutState.setCustomScenarioDescription(scenarioDescriptionTrimmed);
        } else {
            await hangoutState.clearCustomScenarioDescription();
        }

        // 2. Save time of day
        if (timeOfDayString) {
            await hangoutState.setTimeSlot(timeOfDayString);

            try {
                const { timeProgressionService } = await import('../../services/TimeProgressionService.js');
                if (timeProgressionService?.isRunning) {
                    let location = getCachedLocationName(backgroundId, 'Unknown Location');

                    const nightVenues = ['club', 'bar', 'nightclub', 'disco', 'lounge'];
                    const isNightVenue = nightVenues.some(venue => location.toLowerCase().includes(venue));

                    const timeMap = {
                        'day': isNightVenue ? 2 : 14,
                        'sunset': isNightVenue ? 22 : 18,
                        'night': isNightVenue ? 2 : 22
                    };

                    const targetHour = timeMap[timeOfDayString] || (isNightVenue ? 2 : 14);
                    timeProgressionService.setHour(targetHour);
                }
            } catch (error) {
                console.warn('Could not access TimeProgressionService:', error);
            }
        }

        // 3. Save background/location
        if (backgroundId && backgroundId !== '0') {
            try {
                const { getBackgroundService } = await import('../../../../core/services/BackgroundService.js');
                const backgroundService = getBackgroundService();

                const currentBackground = await backgroundService.getCurrentBackground();
                const currentBackgroundId = currentBackground.locationId;

                if (String(currentBackgroundId) !== String(backgroundId)) {
                    let oldLocationName = getCachedLocationName(currentBackgroundId, `Location ${currentBackgroundId}`);
                    let newLocationName = getCachedLocationName(backgroundId, `Location ${backgroundId}`);

                    // setLocation returns getCurrentBackground(), so use the return value directly
                    const updatedBackground = await backgroundService.setLocation(String(backgroundId));
                    // Explicitly update the visual background to ensure it changes immediately
                    try {
                        const { backgroundRenderer } = await import('../../../../core/services/BackgroundRenderer.js');
                        if (updatedBackground.backgroundUrl) {
                            await backgroundRenderer.applyBackgroundImage(updatedBackground.backgroundUrl, { skipPlaceholderCheck: true });
                        }
                    } catch (renderError) {
                        console.warn('[settings-submit] Failed to update background renderer:', renderError);
                    }

                    const locationChangeMessage = {
                        role: 'system',
                        content: `Location changed from ${oldLocationName} to ${newLocationName}.`,
                        path: `location_change_settings_${Date.now()}`,
                        createdAt: Date.now(),
                        isLocationChange: true,
                        metadata: {
                            customScenarioDescription: scenarioDescriptionMetadataValue,
                            timeOfDay: timeOfDayString,
                            oldBackgroundId: currentBackgroundId,
                            newBackgroundId: backgroundId,
                            backgroundUrl: updatedBackground.backgroundUrl,
                            scenarioId: scenario?.id || 1,
                            currentTime: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
                            locationChange: {
                                oldLocationId: currentBackgroundId,
                                newLocationId: backgroundId,
                                oldLocationName: oldLocationName,
                                newLocationName: newLocationName,
                                source: 'settings_modal'
                            }
                        }
                    };

                    await hangoutState.addMessage(locationChangeMessage, false);
                } else {
                    await backgroundService.setLocation(String(backgroundId));
                }

                const sessionToUpdate = hangoutState.getCurrentSession();
                if (sessionToUpdate) {
                    sessionToUpdate.locId = backgroundId;
                    sessionToUpdate.background = backgroundId;
                }
            } catch (error) {
                console.error('Error using BackgroundService, falling back:', error);
                await hangoutState.setSelectedBackground(backgroundId);
            }
        }

        // 4. Save time of day using BackgroundService
        if (timeOfDayString) {
            try {
                const { getBackgroundService } = await import('../../../../core/services/BackgroundService.js');
                const backgroundService = getBackgroundService();
                await backgroundService.setTimeOfDay(timeOfDayString);
                await hangoutState.setTimeSlot(timeOfDayString);
            } catch (error) {
                console.error('Error using BackgroundService for time, falling back:', error);
                await hangoutState.setTimeSlot(timeOfDayString);
            }
        }

        // 5. Save character goals and track changes
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};
        const currentActiveCharacters = resolvePreviousCrewCharacters(hangoutConfig, hangoutState);
        const presentCharacterIds = resolvePresentCharacterIds(currentActiveCharacters, hangoutState);

        // Track changes
        const goalChanges = [];
        const characterChanges = [];

        const currentCharacterIds = collectIdsFromCharacters(currentActiveCharacters);
        const newCharacterIds = collectIdsFromCharacters(convCharacters);

        const addedCharacterIds = [...newCharacterIds].filter((id) => !presentCharacterIds.has(id));
        const addedCharacters = convCharacters.filter((character) => addedCharacterIds.includes(String(character.id)));

        const removedCharacterIds = [...currentCharacterIds].filter((id) => !newCharacterIds.has(id));
        const removedCharacters = currentActiveCharacters.filter((character) => removedCharacterIds.includes(String(character.id)));

        for (const addedChar of addedCharacters) {
            characterChanges.push({
                action: 'add',
                characterId: addedChar.id,
                characterName: addedChar.name,
                outfitId: addedChar.outfitId || 0
            });
        }

        for (const removedChar of removedCharacters) {
            characterChanges.push({
                action: 'remove',
                characterId: removedChar.id,
                characterName: removedChar.name,
                outfitId: removedChar.outfitId || 0
            });
        }

        convCharacters.forEach((newChar) => {
            const currentChar = currentActiveCharacters.find(c => String(c.id) === String(newChar.id));
            const currentGoal = currentChar?.goal || null;
            const newGoal = newChar.goal || null;

            const goalChanged = (currentGoal || '') !== (newGoal || '');

            if (goalChanged) {
                goalChanges.push({
                    characterId: newChar.id,
                    characterName: newChar.name,
                    oldGoal: currentGoal,
                    newGoal: newGoal
                });
            }
        });

        // Create system messages for changes
        for (const goalChange of goalChanges) {
            const goalChangeMessage = {
                role: 'system',
                content: `${goalChange.characterName}'s goal changed from "${goalChange.oldGoal || 'none'}" to "${goalChange.newGoal || 'none'}".`,
                path: `goal_change_settings_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                createdAt: Date.now(),
                isGoalChange: true,
                metadata: {
                    customScenarioDescription: scenarioDescriptionMetadataValue,
                    timeOfDay: timeOfDayString,
                    backgroundId: backgroundId,
                    backgroundUrl: await hangoutState.getSelectedBackground(),
                    scenarioId: scenario?.id || 1,
                    characterId: goalChange.characterId,
                    currentTime: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
                    goalChange: {
                        characterId: goalChange.characterId,
                        characterName: goalChange.characterName,
                        oldGoal: goalChange.oldGoal,
                        newGoal: goalChange.newGoal,
                        source: 'settings_modal'
                    }
                }
            };

            await hangoutState.addMessage(goalChangeMessage, false);
        }

        // Prepare character changes
        const addedCharacterChanges = characterChanges.filter(c => c.action === 'add');
        const removedCharacterChanges = characterChanges.filter(c => c.action === 'remove');
        const addedCharIds = new Set(addedCharacters.map((character) => String(character.id)));

        for (const character of convCharacters) {
            if (!addedCharIds.has(String(character.id))) continue;
            character.actionId = JOIN_MESSAGE_STANDING_ACTION_ID;
            character.spriteState = {
                ...(character.spriteState || {}),
                action: JOIN_MESSAGE_STANDING_ACTION_ID
            };
        }

        const cancelledJoinIds = new Set();
        const leavesToCreate = [];
        const messagesBeforeLeave = hangoutState.getMessages() || [];
        for (const characterChange of removedCharacterChanges) {
            if (shouldCancelRosterPair(messagesBeforeLeave, characterChange.characterId)) {
                cancelledJoinIds.add(String(characterChange.characterId));
            } else {
                leavesToCreate.push(characterChange);
            }
        }

        if (cancelledJoinIds.size > 0) {
            const filteredMessages = messagesBeforeLeave.filter((message) => {
                if (message?.isJoinMessage !== true) return true;
                return !cancelledJoinIds.has(String(message.characterId ?? message.character_id));
            });
            if (filteredMessages.length < messagesBeforeLeave.length) {
                hangoutState.setMessages(filteredMessages);
            }
        }

        for (const characterChange of leavesToCreate) {
            const leaveOutfitId = characterChange.outfitId || 0;
            const characterChangeMessage = {
                role: 'system',
                content: await getTranslation('character_left', { name: characterChange.characterName }),
                characterId: characterChange.characterId,
                characterName: characterChange.characterName,
                actionId: JOIN_MESSAGE_STANDING_ACTION_ID,
                outfitId: leaveOutfitId,
                path: `character_change_settings_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                createdAt: Date.now(),
                isCharacterChange: true,
                isLeaveMessage: true,
                metadata: {
                    customScenarioDescription: scenarioDescriptionMetadataValue,
                    timeOfDay: timeOfDayString,
                    backgroundId: backgroundId,
                    backgroundUrl: await hangoutState.getSelectedBackground(),
                    scenarioId: scenario?.id || 1,
                    characterId: characterChange.characterId,
                    currentTime: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
                    characterChange: {
                        action: 'remove',
                        characterId: characterChange.characterId,
                        characterName: characterChange.characterName,
                        outfitId: characterChange.outfitId || null,
                        source: 'settings_modal'
                    }
                }
            };

            await hangoutState.addMessage(characterChangeMessage, false);
            await appendRosterNotice(characterChangeMessage);
        }

        // Update config FIRST (needed for sprite updates)
        hangoutConfig.allCharacters = convCharacters;
        if (convCharacters.length > 0) {
            const primaryChar = convCharacters[0];
            if (primaryChar.goal) hangoutConfig.goal = primaryChar.goal;
        }

        delete hangoutConfig.characterId;
        delete hangoutConfig.outfitId;

        if (isScenarioDescriptionCustom) {
            hangoutConfig.customGoal = scenarioDescriptionTrimmed;
            hangoutConfig.scenarioDescription = scenarioDescriptionTrimmed;
            hangoutConfig.isScenarioDescriptionCustom = true;
        } else {
            delete hangoutConfig.customGoal;
            delete hangoutConfig.scenarioDescription;
            hangoutConfig.isScenarioDescriptionCustom = false;
        }
        if (backgroundId) hangoutConfig.locId = backgroundId;
        if (timeOfDayString) hangoutConfig.timeOfDay = timeOfDayString;
        hangoutConfig.customGoals = customGoals;

        await storageManager.setItem('currentHangoutConfig', hangoutConfig);

        // Mark goals as custom
        hangoutState.state.customGoals = hangoutState.state.customGoals || {};
        Object.keys(customGoals).forEach(charId => {
            hangoutState.state.customGoals[charId] = Boolean(customGoals[charId]);
        });

        // Save to backend
        try {
            const sessionId = hangoutState.getCurrentSession()?.conversationId;
            const scenarioId = scenario.id || 1;
            const newSessionId = await saveCharacterSelections(convCharacters, sessionId, scenarioId);
            if (newSessionId && newSessionId !== sessionId) {
                hangoutState.state.currentSession = { ...hangoutState.state.currentSession, conversationId: newSessionId };
            }
        } catch (error) {
            console.warn('Could not save to backend:', error);
        }

        // Update character sprites BEFORE creating join messages
        // This ensures containers exist with correct data-character-id for mobile portrait focus
        try {
            const { updateCharacterImage } = await import('../character-manager.js');
            const addedCharIds = new Set(addedCharacters.map((character) => String(character.id)));

            for (let i = 0; i < convCharacters.length; i++) {
                const char = convCharacters[i];
                const isNewlyAdded = addedCharIds.has(String(char.id));

                const resolvedSprite = resolveLatestCharacterSpriteData({
                    messages: hangoutState.getMessages(),
                    characterId: char.id,
                    config: hangoutConfig
                });
                const actionForFetch = isNewlyAdded
                    ? JOIN_MESSAGE_STANDING_ACTION_ID
                    : resolvedSprite.actionId;
                const spriteStateForUpdate = actionForFetch === null || actionForFetch === undefined
                    ? null
                    : String(actionForFetch);

                const spriteUrl = await fetchCharacterSprite(
                    char.id,
                    actionForFetch,
                    char.outfitId,
                    i,
                    true
                );

                await updateCharacterImage(spriteUrl, char.name, i, spriteStateForUpdate, isNewlyAdded);

                if (actionForFetch !== null && actionForFetch !== undefined) {
                    await persistCharacterSpriteMetaInConfig(char.id, {
                        actionId: actionForFetch,
                        spriteState: { action: actionForFetch }
                    });
                    char.actionId = actionForFetch;
                    char.spriteState = { ...(char.spriteState || {}), action: actionForFetch };
                }
            }

            // Trigger slide-in animation for new characters
            if (addedCharacters.length > 0) {
                await new Promise(resolve => setTimeout(resolve, 200));

                try {
                    const { getLocationTransitionManager } = await import('../location-transition.js');
                    const transitionManager = getLocationTransitionManager();
                    await transitionManager.slideInCharacters();
                } catch (error) {
                    console.error('Error triggering slide-in animation:', error);
                }
            }
        } catch (error) {
            console.error('Error updating character sprites:', error);
        }

        // NOW create join messages AFTER sprites are ready
        // This ensures mobile portrait focus sync can find the correct containers
        if (addedCharacterChanges.length > 0) {
            const { getDisplayManager } = await import('../message-utils.js');
            const displayManager = await getDisplayManager();

            // Update turn navigation to include all characters
            if (displayManager?.isAvailable?.() && typeof displayManager.setTurnNavigation === 'function') {
                displayManager.setTurnNavigation(convCharacters);
            }

            for (const characterChange of addedCharacterChanges) {
                const joinOutfitId = characterChange.outfitId || 0;
                const joinMessage = {
                    role: 'system',
                    content: await getTranslation('character_joined', { name: characterChange.characterName }),
                    characterId: characterChange.characterId,
                    characterName: characterChange.characterName,
                    actionId: JOIN_MESSAGE_STANDING_ACTION_ID,
                    outfitId: joinOutfitId,
                    path: `character_join_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    createdAt: Date.now(),
                    isCharacterChange: true,
                    isJoinMessage: true,
                    metadata: {
                        customScenarioDescription: scenarioDescriptionMetadataValue,
                        timeOfDay: timeOfDayString,
                        backgroundId: backgroundId,
                        backgroundUrl: await hangoutState.getSelectedBackground(),
                        scenarioId: scenario?.id || 1,
                        characterId: characterChange.characterId,
                        currentTime: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
                        characterChange: {
                            action: 'add',
                            characterId: characterChange.characterId,
                            characterName: characterChange.characterName,
                            outfitId: characterChange.outfitId || null,
                            source: 'settings_modal'
                        }
                    }
                };

                await hangoutState.addMessage(joinMessage, false);
                await appendRosterNotice(joinMessage);
            }

            // After all join messages, sync focus back to original character (index 0)
            // so their sprite remains visible in mobile portrait mode
            const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;
            if (isMobilePortrait && displayManager?.isAvailable?.() && convCharacters[0]) {
                const originalCharId = String(convCharacters[0].id);
                
                // Remove animation classes from ALL character containers
                // CSS animations override !important rules, so we need to stop them first
                document.querySelectorAll('.character-slide-in, .slide-in-staggered').forEach(el => {
                    el.classList.remove('character-slide-in', 'slide-in-staggered');
                });
                
                // On mobile portrait, hide ALL character containers first, then show only the original
                // This prevents multiple sprites from stacking on top of each other
                document.querySelectorAll('[class*="character-image-"]').forEach(container => {
                    container.classList.remove('currently-speaking');
                    container.style.opacity = '0';
                });
                
                // Find and show original character container
                const originalContainer = document.querySelector(`.character-image-0`);
                if (originalContainer) {
                    originalContainer.style.opacity = '1';
                    originalContainer.style.visibility = 'visible';
                    originalContainer.classList.add('currently-speaking');
                }
                
                if (typeof displayManager._syncMobilePortraitCharacterFocus === 'function') {
                    displayManager._syncMobilePortraitCharacterFocus(originalCharId);
                }
                // Also sync turn cursor to original character
                if (typeof displayManager._syncTurnCursorToCharacterId === 'function') {
                    displayManager._syncTurnCursorToCharacterId(originalCharId);
                }
                // Update displayedCharacterId to match
                displayManager.displayedCharacterId = originalCharId;
            }
        }

        // Handle character departures
        if (removedCharacters.length > 0) {
            const removedCharacterIds = removedCharacters.map(c => String(c.id));
            const { getDisplayManager } = await import('../message-utils.js');
            const displayManager = await getDisplayManager();
            if (displayManager?.isAvailable?.()) {
                if (getCurrentLayoutMode() === LAYOUT_MODES.CHATBOT) {
                    if (typeof displayManager.syncHistory === 'function') {
                        await displayManager.syncHistory(getTranscriptForLayout(hangoutState));
                    }
                } else {
                for (const removedChar of removedCharacters) {
                    if (typeof displayManager.removeTextboxByCharacterId === 'function') {
                        displayManager.removeTextboxByCharacterId(removedChar.id);
                    }
                }
                if (typeof displayManager.setTurnNavigation === 'function') {
                    displayManager.setTurnNavigation(convCharacters);
                }

                const remainingMessages = hangoutState.getMessages().filter(m => 
                    m.role === 'assistant' && 
                    !removedCharacterIds.includes(String(m.characterId)) &&
                    !isRosterReplayMessage(m)
                );
                
                for (const char of convCharacters) {
                    const charId = String(char.id);
                    const hasEntry = displayManager.findTextboxByCharacterId?.(charId);
                    if (!hasEntry) {
                        const lastMsg = remainingMessages.filter(m => 
                            String(m.characterId) === charId
                        ).pop();
                        
                        if (lastMsg?.content && typeof displayManager.createMessage === 'function') {
                            await displayManager.createMessage(
                                lastMsg.content,
                                char.name || lastMsg.characterName || 'Character',
                                charId
                            );
                        }
                    }
                }
                }
                
                if (convCharacters.length > 0 && typeof displayManager.showCanvas === 'function') {
                    displayManager.showCanvas();
                }
            }

            // Cleanup sprite containers
            try {
                const { cleanupRemovedCharacters } = await import('../character-manager.js');
                await cleanupRemovedCharacters();
            } catch (error) {
                console.error('Error with character removal:', error);
            }
        }

        return true;
    } catch (error) {
        console.error('Error saving changes:', error);
        const { showError } = await import('../../../utils/UnifiedNotificationSystem.js');
        showError('Error saving changes. Please try again.', { icon: 'exclamation-triangle', duration: 5000 });
        openModal('settings-modal');
        return false;
    }
}
